import ImagePreloader from '@rio-cloud/rio-uikit/ImagePreloader';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import MediaDialog from '@rio-cloud/rio-uikit/MediaDialog';
import useWindowResize from '@rio-cloud/rio-uikit/useWindowResize';
import { SCREEN_SM } from '@rio-cloud/rio-uikit/DeviceUtils';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useCustomIntl } from '../i18n/i18n';

type ImagePreloaderChildProps = {
  image: { src: string };
};

type ImageLoaderProps = {
  src: string;
  alt?: string;
  className?: string;
  enlargeable?: boolean;
};

const isSmallScreen = () =>
  window.matchMedia(`(max-width: ${SCREEN_SM}px)`).matches;

export const ImageLoader = (props: ImageLoaderProps) => {
  const { src, alt, className, enlargeable = false } = props;
  const { formatMessage } = useCustomIntl();
  const [isEnlarged, setIsEnlarged] = useState(false);
  const [isMobileView, setIsMobileView] = useState(isSmallScreen());

  useWindowResize(() => setIsMobileView(isSmallScreen()));

  const classes = classNames('', className);

  const openEnlarged = () => {
    if (isMobileView) {
      // On small screens the dialog can't show a wide image much larger than
      // inline. Open it in a new tab so the browser's native viewer allows
      // pinch-to-zoom and panning.
      window.open(src, '_blank', 'noopener,noreferrer');
      return;
    }
    setIsEnlarged(true);
  };

  // The RIO Dialog has no built-in "click outside to close". While the enlarged
  // dialog is open, close it when the user clicks on the modal backdrop.
  // Escape is handled in the capture phase so it closes only the enlarged image
  // and does not also reach the surrounding user manual dialog's Esc handler
  // (the uikit registers all dialog Esc handlers on a shared document listener).
  useEffect(() => {
    if (!isEnlarged) {
      return;
    }
    const closeOnBackdropClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Only react to the enlarged media dialog's own backdrop, not the
      // backdrop of the surrounding user manual dialog underneath it.
      if (
        target.classList.contains('modal-backdrop') &&
        target.parentElement?.classList.contains('media-dialog')
      ) {
        setIsEnlarged(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopImmediatePropagation();
        setIsEnlarged(false);
      }
    };
    document.addEventListener('click', closeOnBackdropClick);
    document.addEventListener('keydown', closeOnEscape, true);
    return () => {
      document.removeEventListener('click', closeOnBackdropClick);
      document.removeEventListener('keydown', closeOnEscape, true);
    };
  }, [isEnlarged]);

  const renderImage = (imageSrc: string) => {
    if (!enlargeable) {
      return (
        <img
          className={`img-responsive ${classes}`}
          src={imageSrc}
          alt={alt}
        />
      );
    }
    const enlargeLabel = formatMessage({ id: 'userManual.image.enlarge' });
    return (
      <img
        className={`img-responsive cursor-pointer ${classes}`}
        src={imageSrc}
        alt={alt}
        role="button"
        tabIndex={0}
        aria-label={enlargeLabel}
        onClick={openEnlarged}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openEnlarged();
          }
        }}
      />
    );
  };

  return (
    <>
      <ImagePreloader src={src}>
        {({ image }: ImagePreloaderChildProps) => {
          if (image) {
            return renderImage(image.src);
          }
          return (
            <div className={`aspect-ratio-resizer ${classes}`}>
              <svg viewBox={'0 0 970 347'} />
              <div>
                <div className={'content-center height-100pct'}>
                  <Spinner isInverse={false} />
                </div>
              </div>
            </div>
          );
        }}
      </ImagePreloader>
      {enlargeable && isEnlarged && (
        <MediaDialog
          show={isEnlarged}
          onClose={() => setIsEnlarged(false)}
          showCloseButton
          disableEsc
          bsSize="xl"
          className="manual-image-dialog"
          media={[{ type: 'image', src, title: alt ?? '' }]}
          previousButtonText=""
          nextButtonText=""
        />
      )}
    </>
  );
};
